import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from '../Button';

type LogoutConfirmationModalProps = {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

export default function LogoutConfirmationModal({ isOpen, onConfirm, onCancel }: LogoutConfirmationModalProps) {
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onCancel();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onCancel]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 transition-opacity duration-300"
            onClick={onCancel}
        >
            <div
                className="w-full max-w-md scale-100 transform rounded-xl border border-gray-200 bg-white p-6 shadow-2xl transition-all duration-300 dark:border-gray-700 dark:bg-gray-800"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4 text-center">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
                        <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                    </div>
                </div>

                <h2 className="mb-2 text-center text-xl font-bold text-gray-900 dark:text-gray-100">
                    Confirm Logout
                </h2>
                <p className="mb-8 text-center text-sm text-gray-600 dark:text-gray-400">
                    Are you sure you want to exit? You will need to login again to access your account.
                </p>

                <div className="flex gap-3">
                    <Button
                        variant="secondary"
                        onClick={onCancel}
                        className="flex-1 font-semibold py-2.5"
                    >
                        No, Stay
                    </Button>
                    <Button
                        variant="primary"
                        onClick={onConfirm}
                        className="flex-1 bg-red-600 hover:bg-red-700 focus:ring-red-500 font-semibold py-2.5 shadow-lg shadow-red-500/20"
                    >
                        Yes, Logout
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
}
